// The job-application pipeline: applied → communication → interview → contract.
//
// One row per application, and the row IS the timeline — the four stages are
// drawn as a track with each reached stage carrying the date and what moved it.
// That is the question this page exists to answer ("where did each of these get
// to"), and burying it behind a click would mean scanning by hand.
//
// Two things share the page because they are the two things that block it:
//   • applications waiting on a screening question nobody has answered yet
//   • the answer bank itself, where answering one releases everything waiting
//     on it
//
// Nothing here generates an answer. The bank is what a human approved, and an
// unmatched question parks the application rather than being guessed at — see
// utils/jobAnswers on the server.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { get, post } from "api/index";

export const STAGES = ["applied", "communication", "interview", "contract"];

// The boards this channel runs against. Labelled rather than shown raw so the
// column reads as a name, not a database value.
export const BOARD_LABEL = {
  indeed: "Indeed",
  linkedin: "LinkedIn",
};

export const STAGE_LABEL = {
  applied: "Applied",
  communication: "In touch",
  interview: "Interview set",
  contract: "Contract",
};

// One colour per stage, warming as it advances, so a glance down the column
// reads as progress.
export const STAGE_COLOR = {
  applied: "#94a3b8",
  communication: "#0891b2",
  interview: "#7c3aed",
  contract: "#16a34a",
};

const CLOSED_LABEL = { rejected: "Rejected", withdrawn: "Withdrawn", "no-response": "No response" };

export function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getUTCDate()).padStart(2, "0")} ${d.toLocaleString("en", { month: "short", timeZone: "UTC" })}`;
}

/**
 * How far along a row is, as an index. A closed row keeps the stage it died at
 * — a rejection is not a fifth stage, and treating it as one would make every
 * count wrong.
 */
export function stageIndex(stage) {
  return STAGES.indexOf(stage);
}

/** The timeline entry that put a row into a given stage, if any. */
export function entryFor(timeline, stage) {
  return (timeline || []).filter((t) => t.stage === stage).slice(-1)[0] || null;
}

function StageTrack({ application }) {
  const reached = stageIndex(application.stage);
  return (
    <div className="jp-track" data-testid="stage-track">
      {STAGES.map((s, i) => {
        const done = i <= reached;
        const entry = entryFor(application.timeline, s);
        return (
          <div
            key={s}
            className={`jp-step ${done ? "is-done" : ""}`}
            data-stage={s}
            data-done={done ? "1" : "0"}
            style={done ? { borderColor: STAGE_COLOR[s], color: STAGE_COLOR[s] } : undefined}
            // What moved it, on hover. `by` matters: an auto-detected move is a
            // claim the operator may want to check, and the evidence is there.
            title={entry ? `${STAGE_LABEL[s]} — ${fmtDate(entry.at)}${entry.by ? ` (${entry.by})` : ""}${entry.note ? `: ${entry.note}` : ""}` : STAGE_LABEL[s]}
          >
            <span className="jp-step-label">{STAGE_LABEL[s]}</span>
            {entry ? <span className="jp-step-date">{fmtDate(entry.at)}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

export default function JobPipeline({ brandId }) {
  const [rows, setRows] = useState([]);
  const [byStage, setByStage] = useState({});
  const [closedCounts, setClosedCounts] = useState({});
  const [stageFilter, setStageFilter] = useState("all");
  const [answers, setAnswers] = useState([]);
  const [pending, setPending] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState({ question: "", answer: "" });
  const [openRow, setOpenRow] = useState("");

  const scope = brandId ? `&brandId=${encodeURIComponent(brandId)}` : "";

  const load = useCallback(async () => {
    setError("");
    try {
      const [apps, bank] = await Promise.all([
        get(`/job-channel/applications?stage=${stageFilter}${scope}`, false),
        get(`/job-channel/answers?1=1${scope}`, false),
      ]);
      setRows((apps && apps.items) || []);
      setByStage((apps && apps.byStage) || {});
      setClosedCounts((apps && apps.closed) || {});
      setAnswers((bank && bank.items) || []);
      setPending((bank && bank.pending) || {});
    } catch (e) {
      // Say so rather than rendering an empty pipeline, which reads as "nothing
      // has happened" — the opposite of what a failed load means.
      setError((e && e.message) || "could not load the pipeline");
    }
  }, [stageFilter, scope]);

  useEffect(() => { load(); }, [load]);

  const move = async (id, body) => {
    setBusy(true);
    try {
      await post(`/job-channel/applications/${id}/stage`, body);
      await load();
    } catch (e) {
      setError((e && e.message) || "could not move that row");
    } finally { setBusy(false); }
  };

  const saveAnswer = async () => {
    if (!draft.question.trim()) return;
    setBusy(true);
    try {
      await post("/job-channel/answers", { brandId, question: draft.question, answer: draft.answer });
      setDraft({ question: "", answer: "" });
      await load();
    } catch (e) {
      setError((e && e.message) || "could not save that answer");
    } finally { setBusy(false); }
  };

  // Questions blocking at least one application, commonest first — answering
  // the top one unblocks the most work.
  const waiting = useMemo(
    () => Object.entries(pending).sort((a, b) => b[1] - a[1]),
    [pending],
  );

  const totals = useMemo(() => {
    const live = STAGES.reduce((n, s) => n + (byStage[s] || 0), 0);
    const closed = Object.values(closedCounts).reduce((n, v) => n + v, 0);
    return { live, closed };
  }, [byStage, closedCounts]);

  return (
    <div className="job-pipeline">
      <div className="jp-head">
        <h3>Job applications</h3>
        <div className="jp-counts" data-testid="jp-counts">
          {STAGES.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`jp-count-${s}`}
              className={`jp-count ${stageFilter === s ? "is-on" : ""}`}
              style={{ borderBottomColor: STAGE_COLOR[s] }}
              onClick={() => setStageFilter(stageFilter === s ? "all" : s)}
            >
              <b>{byStage[s] || 0}</b>
              <span>{STAGE_LABEL[s]}</span>
            </button>
          ))}
          {Object.entries(closedCounts).map(([k, v]) => (
            <span key={k} className="jp-count is-closed" data-testid={`jp-closed-${k}`}>
              <b>{v}</b><span>{CLOSED_LABEL[k] || k}</span>
            </span>
          ))}
        </div>
      </div>

      {error ? <p className="jp-error" data-testid="jp-error">{error}</p> : null}

      {waiting.length ? (
        <div className="jp-waiting" data-testid="jp-waiting">
          <h4>Waiting on an answer</h4>
          <p className="jp-hint">
            These applications are filled in and paused. Nothing is submitted with a
            guessed answer — answer once and every application waiting on it goes.
          </p>
          <ul>
            {waiting.map(([q, n]) => (
              <li key={q}>
                <span className="jp-q">{q}</span>
                <span className="jp-n">{n} waiting</span>
                <button type="button" onClick={() => setDraft({ question: q, answer: "" })}>Answer</button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="jp-bank" data-testid="jp-bank">
        <h4>Approved answers <span className="jp-hint">({answers.length})</span></h4>
        <div className="jp-bank-form">
          <input
            data-testid="jp-answer-question"
            placeholder="Screening question, exactly as it is asked"
            value={draft.question}
            onChange={(e) => setDraft({ ...draft, question: e.target.value })}
          />
          <input
            data-testid="jp-answer-answer"
            placeholder="Your answer"
            value={draft.answer}
            onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
          />
          <button type="button" data-testid="jp-answer-save" disabled={busy || !draft.question.trim()} onClick={saveAnswer}>
            Approve
          </button>
        </div>
      </div>

      {!rows.length ? (
        <p data-testid="jp-empty" className="jp-empty">
          {totals.live || totals.closed ? "Nothing at this stage." : "No applications yet."}
        </p>
      ) : (
        <table className="jp-table">
          <thead>
            <tr><th>Role</th><th>Employer</th><th>Progress</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const open = openRow === r._id;
              return (
                <React.Fragment key={r._id}>
                  <tr data-testid="jp-row" className={r.closed ? "is-closed" : ""}>
                    <td>
                      {r.url
                        ? <a href={r.url} target="_blank" rel="noopener noreferrer">{r.title}</a>
                        : r.title}
                    </td>
                    <td>
                      {r.employer}
                      {/* Which board this came from. Only worth the pixels
                          because there are now two: the same role search runs
                          against Indeed and LinkedIn with independent pacing,
                          and "why is this one still held" usually has the
                          board as its answer. */}
                      {r.board ? <span className="jp-badge jp-board" data-testid="jp-board">{BOARD_LABEL[r.board] || r.board}</span> : null}
                      <span className="jp-loc">{r.location}</span>
                    </td>
                    <td><StageTrack application={r} /></td>
                    <td>
                      {r.closed
                        ? <span className="jp-badge is-closed">{CLOSED_LABEL[r.closed] || r.closed}</span>
                        : <span className="jp-badge" data-testid="jp-status">{r.status}</span>}
                      {(r.unanswered || []).length
                        ? <span className="jp-badge is-warn" data-testid="jp-needs">{r.unanswered.length} unanswered</span>
                        : null}
                    </td>
                    <td className="jp-actions">
                      <button type="button" onClick={() => setOpenRow(open ? "" : r._id)} data-testid="jp-open">
                        {open ? "Hide" : "Detail"}
                      </button>
                      {!r.closed && stageIndex(r.stage) < STAGES.length - 1 ? (
                        <button
                          type="button"
                          data-testid="jp-advance"
                          disabled={busy}
                          onClick={() => move(r._id, { stage: STAGES[stageIndex(r.stage) + 1] })}
                        >
                          → {STAGE_LABEL[STAGES[stageIndex(r.stage) + 1]]}
                        </button>
                      ) : null}
                      {!r.closed ? (
                        <button type="button" data-testid="jp-reject" disabled={busy} onClick={() => move(r._id, { closed: "rejected" })}>
                          Rejected
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  {open ? (
                    <tr data-testid="jp-detail">
                      <td colSpan={5}>
                        <div className="jp-detail">
                          <div>
                            <h5>Timeline</h5>
                            <ol>
                              {(r.timeline || []).map((t, i) => (
                                <li key={i}>
                                  <b>{STAGE_LABEL[t.stage] || t.stage}</b>
                                  <span>{fmtDate(t.at)}</span>
                                  {/* WHO moved it. An auto-detected move is a claim,
                                      and the evidence behind it is worth showing. */}
                                  <em>{t.by}</em>
                                  {t.note ? <span className="jp-note">{t.note}</span> : null}
                                </li>
                              ))}
                            </ol>
                          </div>
                          <div>
                            <h5>What we told them</h5>
                            {(r.answers || []).length ? (
                              <dl data-testid="jp-answers">
                                {r.answers.map((a, i) => (
                                  <React.Fragment key={i}>
                                    <dt>{a.question}</dt>
                                    <dd>{a.answer}</dd>
                                  </React.Fragment>
                                ))}
                              </dl>
                            ) : <p className="jp-hint">No screening questions.</p>}
                            {(r.unanswered || []).length ? (
                              <>
                                <h5>Still unanswered</h5>
                                <ul data-testid="jp-unanswered">
                                  {r.unanswered.map((q) => <li key={q}>{q}</li>)}
                                </ul>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
